import { readonly, ref } from 'vue'
import { publicApi, publicLiveCountEventsURL } from '@/api/public'
import {
  normalizePublicLiveCount,
  parsePublicLiveCountEvent,
} from '@/lib/publicLiveCountWire'

export type PublicLiveCountStreamState = 'idle' | 'connecting' | 'open' | 'error'

const reconcileIntervalMs = 15_000

const count = ref(0)
const streamState = ref<PublicLiveCountStreamState>('idle')
const error = ref<string | null>(null)

let started = false
let sessionActive = false
let sessionGeneration = 0
let updateVersion = 0
let source: EventSource | null = null
let reconcileTimer: number | undefined
let refreshController: AbortController | null = null
let refreshInFlight: Promise<void> | null = null
let refreshInFlightGeneration = -1

export const publicLiveCountState = {
  count: readonly(count),
  streamState: readonly(streamState),
  error: readonly(error),
}

export function startPublicLiveCount(): void {
  if (started || typeof window === 'undefined' || typeof document === 'undefined') return
  started = true
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pagehide', suspendSession)
  window.addEventListener('pageshow', resumeSession)
  resumeSession()
}

export function stopPublicLiveCount(): void {
  if (!started) return
  started = false
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('pagehide', suspendSession)
  window.removeEventListener('pageshow', resumeSession)
  suspendSession()
}

export function refreshPublicLiveCount(): Promise<void> {
  if (!sessionActive) return Promise.resolve()
  const generation = sessionGeneration
  if (refreshInFlight && refreshInFlightGeneration === generation) return refreshInFlight

  const controller = new AbortController()
  refreshController = controller
  const requestedAtVersion = updateVersion
  const pending = publicApi.liveCount({ signal: controller.signal })
    .then(response => {
      if (
        sessionActive &&
        generation === sessionGeneration &&
        requestedAtVersion === updateVersion
      ) {
        applyCount(normalizePublicLiveCount(response.count))
      }
    })
    .catch(cause => {
      if (!isAbortError(cause) && generation === sessionGeneration) {
        error.value = errorMessage(cause)
      }
    })
    .finally(() => {
      if (refreshController === controller) refreshController = null
      if (refreshInFlightGeneration === generation) {
        refreshInFlight = null
        refreshInFlightGeneration = -1
      }
    })
  refreshInFlight = pending
  refreshInFlightGeneration = generation
  return pending
}

function handleVisibilityChange(): void {
  if (document.hidden) {
    suspendSession()
    return
  }
  resumeSession()
}

function resumeSession(): void {
  if (!started || document.hidden) return
  if (sessionActive) {
    if (!source) openEventSource()
    void refreshPublicLiveCount()
    return
  }
  sessionActive = true
  sessionGeneration += 1
  streamState.value = 'connecting'
  error.value = null
  openEventSource()
  void refreshPublicLiveCount()
  reconcileTimer = window.setInterval(() => {
    if (!sessionActive || document.hidden) return
    if (!source) openEventSource()
    void refreshPublicLiveCount()
  }, reconcileIntervalMs)
}

function suspendSession(): void {
  if (!sessionActive) return
  sessionActive = false
  sessionGeneration += 1
  refreshController?.abort()
  refreshController = null
  refreshInFlight = null
  refreshInFlightGeneration = -1
  source?.close()
  source = null
  if (reconcileTimer !== undefined) {
    window.clearInterval(reconcileTimer)
    reconcileTimer = undefined
  }
  streamState.value = 'idle'
}

function openEventSource(): void {
  if (source || !sessionActive) return
  let nextSource: EventSource
  try {
    nextSource = new EventSource(publicLiveCountEventsURL)
    source = nextSource
  } catch (cause) {
    streamState.value = 'error'
    error.value = errorMessage(cause)
    return
  }

  nextSource.onopen = () => {
    if (source !== nextSource || !sessionActive) return
    streamState.value = 'open'
    error.value = null
    // onopen fires again after EventSource reconnects. Reconcile against the
    // no-store snapshot even though every connection also receives a primer.
    void refreshPublicLiveCount()
  }
  nextSource.onmessage = event => {
    if (source !== nextSource || !sessionActive) return
    const nextCount = parsePublicLiveCountEvent(event.data)
    if (nextCount === null) return
    applyCount(nextCount)
  }
  nextSource.onerror = () => {
    if (source !== nextSource || !sessionActive) return
    streamState.value = 'error'
    error.value = 'Связь со счётчиком LIVE прервана. Переподключаемся…'
    void refreshPublicLiveCount()
  }
}

function applyCount(nextCount: number): void {
  updateVersion += 1
  count.value = nextCount
  error.value = null
}

function isAbortError(value: unknown): boolean {
  return value instanceof Error && value.name === 'AbortError'
}

function errorMessage(value: unknown): string {
  return value instanceof Error ? value.message : String(value)
}
